import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, TextField, Button,
  CircularProgress, Alert, Stepper, Step, StepLabel
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { richiediOTPCorso, verificaOTPCorso } from "../utils/otpClient";

export default function LoginClienteCorso() {
  const { corsoId } = useParams();
  const navigate = useNavigate();
  const [corso, setCorso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [elaborando, setElaborando] = useState(false);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem(`clienteTokenCorso_${corsoId}`);
    if (token) {
      navigate(`/report-corso/${corsoId}`);
      return;
    }
    getDoc(doc(db, "corsi", corsoId)).then((snap) => {
      if (snap.exists()) setCorso({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
  }, [corsoId]);

  async function handleRichiediOTP() {
    setElaborando(true);
    setErrore(null);
    try {
      await richiediOTPCorso(email, corsoId);
      setStep(1);
    } catch (err) {
      setErrore(err.message);
    }
    setElaborando(false);
  }

  async function handleVerificaOTP() {
    setElaborando(true);
    setErrore(null);
    try {
      const result = await verificaOTPCorso(email, corsoId, otp);
      sessionStorage.setItem(`clienteTokenCorso_${corsoId}`, result.token);
      navigate(`/report-corso/${corsoId}`);
    } catch (err) {
      setErrore(err.message);
    }
    setElaborando(false);
  }

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box display="flex" justifyContent="center" alignItems="center"
      minHeight="100vh" sx={{ backgroundColor: "#f0f4ff" }}>
      <Paper elevation={3} sx={{ p: 5, maxWidth: 440, width: "100%" }}>

        {/* Logo */}
        <Box mb={3} display="flex" justifyContent="center">
          <svg width="160" viewBox="0 0 200 100" role="img">
            <defs>
              <linearGradient id="lgcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E63329"/>
                <stop offset="50%" stopColor="#9B2DB5"/>
                <stop offset="100%" stopColor="#1A56A0"/>
              </linearGradient>
              <linearGradient id="lgcBarGrad" x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.4"/>
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0.95"/>
              </linearGradient>
            </defs>
            <rect x="4" y="4" width="60" height="60" rx="10" fill="url(#lgcGrad)"/>
            <rect x="18" y="0" width="5" height="10" rx="2.5" fill="#ffffff" opacity="0.9"/>
            <rect x="44" y="0" width="5" height="10" rx="2.5" fill="#ffffff" opacity="0.9"/>
            <rect x="10" y="38" width="6" height="18" rx="2" fill="url(#lgcBarGrad)"/>
            <rect x="20" y="30" width="6" height="26" rx="2" fill="url(#lgcBarGrad)"/>
            <rect x="30" y="22" width="6" height="34" rx="2" fill="url(#lgcBarGrad)"/>
            <rect x="40" y="28" width="6" height="28" rx="2" fill="url(#lgcBarGrad)"/>
            <rect x="50" y="16" width="6" height="40" rx="2" fill="url(#lgcBarGrad)"/>
            <polyline points="13,38 23,30 33,22 43,28 53,16"
              fill="none" stroke="#F59E0B" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="53" cy="16" r="2.5" fill="#F59E0B"/>
            <text x="74" y="22" fontFamily="sans-serif" fontSize="11" fontWeight="600" fill="#1A56A0">Dashboard</text>
            <text x="74" y="36" fontFamily="sans-serif" fontSize="11" fontWeight="600" fill="#1A56A0">Gestione</text>
            <text x="74" y="50" fontFamily="sans-serif" fontSize="11" fontWeight="600" fill="#1A56A0">Eventi</text>
            <line x1="4" y1="74" x2="196" y2="74" stroke="#e0e0e0" strokeWidth="1"/>
            <text x="100" y="88" textAnchor="middle" fontFamily="sans-serif"
              fontSize="8.5" fill="#888888" letterSpacing="1">
              Virtual Meeting Assistant
            </text>
          </svg>
        </Box>

        {corso && (
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
            {corso.nome}
          </Typography>
        )}

        <Stepper activeStep={step} sx={{ mb: 4 }}>
          <Step><StepLabel>Inserisci email</StepLabel></Step>
          <Step><StepLabel>Verifica codice</StepLabel></Step>
        </Stepper>

        {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

        {step === 0 && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Inserisci l'indirizzo email con cui sei registrato come referente di questo corso.
              Riceverai un codice di accesso via email.
            </Typography>
            <TextField
              fullWidth label="Email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRichiediOTP()}
              sx={{ mb: 2 }}
            />
            <Button fullWidth variant="contained" size="large"
              onClick={handleRichiediOTP}
              disabled={elaborando || !email.trim()}
              sx={{ backgroundColor: "#1A56A0" }}>
              {elaborando ? <CircularProgress size={20} color="inherit" /> : "Invia codice"}
            </Button>
          </>
        )}

        {step === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Abbiamo inviato un codice a <strong>{email}</strong>.
              Inseriscilo qui sotto per accedere al report.
            </Typography>
            <TextField
              fullWidth label="Codice OTP" value={otp}
              onChange={(e) => setOtp(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleVerificaOTP()}
              inputProps={{ maxLength: 6, style: { letterSpacing: "8px", fontSize: "24px" } }}
              sx={{ mb: 2 }}
            />
            <Button fullWidth variant="contained" size="large"
              onClick={handleVerificaOTP}
              disabled={elaborando || otp.length !== 6}
              sx={{ backgroundColor: "#1A56A0", mb: 1 }}>
              {elaborando ? <CircularProgress size={20} color="inherit" /> : "Accedi"}
            </Button>
            <Button fullWidth variant="text" size="small"
              onClick={() => { setStep(0); setOtp(""); setErrore(null); }}>
              Cambia email
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
